import { styled } from "goober";
import { AskMonitoringChat } from "./AskMonitoringChat";
import { useMemo } from "react";
import {
  type DisplayableSpace,
  useSpaceDisplayName,
} from "../../hooks/useSpaceDisplayName";
import { navigate, routes } from "../../lib/urls";
import { useMonitoringInteraction } from "./analytics";
import { GhostPill } from "../GhostPill";
import { PressButton } from "../PressButton";
import { MetaSep } from "../MetaSeparator";
import { Text } from "../Text";
import {
  computeSpaceExecutionCountRows,
  computeWorkflowExecutionCountRows,
  formatExecutionCount,
  workflowRowsForSpaceSegment,
} from "./executionCounts";
import {
  Bar,
  BarColumn,
  BarRow,
  barColumnStyles,
  ColumnMeta,
  FadeScrollColumns,
  logBarFraction,
} from "./monitoringBars";
import { PanelBadge } from "./PanelInfo";
import { splitBySharePrefix } from "./foldSegments";
import { spaceColorMap } from "./monitoringPanelLayout";
import { OTHER_SPACES_KEY } from "./spaceSegments";
import {
  foldsPersonal,
  PERSONAL_BUCKET,
  PERSONAL_BUCKET_COLOR,
} from "./monitoringScope";
import { useMonitoringScope } from "./useMonitoringScope";
import {
  useMonitoringSpaces,
  useMonitoringWorkflows,
} from "./useMonitoringWorkflows";
import { useSpaceDrilldown } from "./useSpaceDrilldown";
import { WorkflowListModal } from "./WorkflowListModal";
import { Panel, PanelHeader, PanelSkeleton, TotalRow } from "./workflowsLayout";

// Interactive column (opens the per-space modal); the visuals are shared with
// Spend-by-model via barColumnStyles / the other primitives in monitoringBars.
const Column = styled(PressButton)`
  ${barColumnStyles}
`;

// Widest bar row (grid-column: span 2) fits far more bars than the dense donuts,
// so it folds only a long tail instead of reusing the tighter shared SPACE_FOLD.
const EXECUTIONS_FOLD = { minShare: 0, maxKept: 12 };

const GHOST_COLUMNS = [
  { key: "ghost-1", count: 1200, labelWidth: 48, countWidth: 26 },
  { key: "ghost-2", count: 640, labelWidth: 40, countWidth: 22 },
  { key: "ghost-3", count: 210, labelWidth: 56, countWidth: 20 },
  { key: "ghost-4", count: 80, labelWidth: 36, countWidth: 18 },
  { key: "ghost-5", count: 12, labelWidth: 44, countWidth: 16 },
];

type SpaceRow = {
  spaceId: string;
  space: DisplayableSpace | null;
  label: string;
  executionCount: number;
  workflowCount: number;
};

export function ExecutionsBySpace() {
  const recordInteraction = useMonitoringInteraction();
  const { report, loading } = useMonitoringWorkflows();
  const { spaces, loading: spacesLoading } = useMonitoringSpaces();
  const { scope, personalIds, matchesSpace } = useMonitoringScope(
    routes.monitoring.workflows
  );
  const displayName = useSpaceDisplayName();
  const { open, selectedSpaceId, search, setSearch, close } =
    useSpaceDrilldown("executions");

  const { chartRows, totalExecutions, workflowRows, foldedSpaceIds } =
    useMemo(() => {
      const inScope = spaces.filter((space) => matchesSpace(space.id));
      const inScopeIds = inScope.map((space) => space.id);
      const workflowRows = computeWorkflowExecutionCountRows(report.workflows);
      const executionsBySpace = computeSpaceExecutionCountRows(
        workflowRows,
        inScopeIds
      );

      const rows: SpaceRow[] = [];
      let personalExec = 0;
      let personalWf = 0;
      for (const row of executionsBySpace) {
        if (foldsPersonal(scope) && personalIds.has(row.spaceId)) {
          personalExec += row.executionCount;
          personalWf += row.workflowCount;
          continue;
        }
        const space = inScope.find((entry) => entry.id === row.spaceId)!;
        rows.push({
          spaceId: row.spaceId,
          space,
          label: displayName(space),
          executionCount: row.executionCount,
          workflowCount: row.workflowCount,
        });
      }
      if (personalExec > 0) {
        rows.push({
          spaceId: PERSONAL_BUCKET,
          space: null,
          label: "Personal",
          executionCount: personalExec,
          workflowCount: personalWf,
        });
      }
      rows.sort(
        (a, b) =>
          b.executionCount - a.executionCount || a.label.localeCompare(b.label)
      );

      const totalExecutions = rows.reduce(
        (sum, row) => sum + row.executionCount,
        0
      );

      // Fold the small-execution tail into one "Other" bar; its drill-in lists the
      // folded spaces' workflows so it isn't a dead end.
      const { kept, folded } = splitBySharePrefix(
        rows,
        (row) => row.executionCount,
        totalExecutions,
        EXECUTIONS_FOLD
      );
      const foldedSpaceIds = new Set<string>();
      for (const row of folded) {
        if (row.spaceId === PERSONAL_BUCKET)
          for (const id of personalIds) foldedSpaceIds.add(id);
        else foldedSpaceIds.add(row.spaceId);
      }
      const chartRows: SpaceRow[] =
        folded.length > 0
          ? [
              ...kept,
              {
                spaceId: OTHER_SPACES_KEY,
                space: null,
                label: `${folded.length} more spaces`,
                executionCount: folded.reduce(
                  (sum, row) => sum + row.executionCount,
                  0
                ),
                workflowCount: folded.reduce(
                  (sum, row) => sum + row.workflowCount,
                  0
                ),
              },
            ]
          : [...kept];

      return { chartRows, totalExecutions, workflowRows, foldedSpaceIds };
    }, [report, spaces, scope, personalIds, matchesSpace, displayName]);

  const selectedRow = chartRows.find((row) => row.spaceId === selectedSpaceId);
  const selectedWorkflowRows = useMemo(() => {
    if (!selectedRow) return [];
    return workflowRowsForSpaceSegment(
      workflowRows,
      selectedRow.spaceId,
      personalIds,
      foldedSpaceIds
    );
  }, [selectedRow, workflowRows, personalIds, foldedSpaceIds]);
  const colorMap = useMemo(() => {
    const map = new Map(spaceColorMap(spaces));
    map.set(PERSONAL_BUCKET, PERSONAL_BUCKET_COLOR);
    map.set(OTHER_SPACES_KEY, "var(--gray-600)");
    return map;
  }, [spaces]);
  const maxCount = Math.max(1, ...chartRows.map((row) => row.executionCount));

  if (loading || spacesLoading) {
    return <PanelSkeleton />;
  }

  return (
    <Panel>
      <PanelHeader>
        <Text variant="text-small-md">Executions by space</Text>
        <TotalRow>
          <Text variant="display-md">
            {formatExecutionCount(totalExecutions)}
          </Text>
          <PanelBadge>Last 30 days</PanelBadge>
        </TotalRow>
      </PanelHeader>

      <FadeScrollColumns ariaHidden={chartRows.length === 0}>
        {chartRows.length === 0
          ? GHOST_COLUMNS.map((row) => (
              <BarColumn key={row.key}>
                <BarRow>
                  <Bar
                    style={{
                      width: 6,
                      background: "var(--alpha8)",
                      minHeight: 6,
                      height: `${logBarFraction(row.count, GHOST_COLUMNS[0]!.count) * 100}%`,
                    }}
                  />
                </BarRow>
                <ColumnMeta>
                  <GhostPill style={{ width: row.labelWidth }} />
                  <GhostPill style={{ width: row.countWidth }} />
                </ColumnMeta>
              </BarColumn>
            ))
          : chartRows.map((row) => (
              <Column
                key={row.spaceId}
                data-bar-column
                onPress={() => {
                  recordInteraction("workflows.panel_drilldown_open", {
                    targetType: "space",
                    targetId: row.spaceId,
                    value: "executions",
                  });
                  navigate(routes.monitoring.workflows, {
                    panel: "executions",
                    space: row.spaceId,
                    search: "",
                  });
                }}
              >
                <BarRow>
                  <Bar
                    data-bar
                    style={{
                      background: colorMap.get(row.spaceId)!,
                      minHeight: row.executionCount > 0 ? 6 : 0,
                      height: `${logBarFraction(row.executionCount, maxCount) * 100}%`,
                    }}
                  />
                </BarRow>
                <ColumnMeta>
                  <Text
                    variant="text-small"
                    color="var(--text-secondary)"
                    ellipsis
                  >
                    {row.label}
                  </Text>
                  <Text
                    variant="text-small-md"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    {formatExecutionCount(row.executionCount)}
                  </Text>
                </ColumnMeta>
              </Column>
            ))}
      </FadeScrollColumns>

      <WorkflowListModal
        open={open}
        onClose={close}
        icon="play"
        title="Executions"
        space={selectedRow?.space ?? undefined}
        sectionTitle="Busiest workflows"
        description="Ranked by executions on the live branch over the last 30 days."
        rows={selectedWorkflowRows}
        search={search}
        onSearchChange={setSearch}
        emptyMessage="No live-branch runs in this space in the last 30 days."
        renderFooter={() =>
          selectedRow ? (
            <>
              {formatExecutionCount(selectedRow.executionCount)} executions
              <MetaSep />
              {selectedRow.workflowCount}{" "}
              {selectedRow.workflowCount === 1 ? "workflow" : "workflows"}
            </>
          ) : null
        }
      />
      <AskMonitoringChat
        surface="executions"
        snapshot={`${totalExecutions} live-branch executions in the last 30 days`}
      />
    </Panel>
  );
}
